import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DocumentEntity, DocumentTypeEnum, FeatureFlagController } from '@domain';
import { RaIconComponent, RaModalComponent } from 'ra-ui';
import { Observable } from 'rxjs';
import { DocumentController } from 'src/app/domain/controllers/document/document.controller';
import { DocumentCreateController } from 'src/app/domain/controllers/document/document-create.controller';
import { InternalTemplateController } from 'src/app/domain/controllers/internal-template.controller';
import { TemplateController } from 'src/app/domain/controllers/template.controller';
import { InternalTemplateEntity } from 'src/app/domain/entities/internal-template.entity';
import { TemplateEntity } from 'src/app/domain/entities/template.entity';
import { isAiEditFeatureEnabled } from 'src/app/modules/document/pages/edit/services/edit-mode.service';
import { AiEditKickoffService } from 'src/app/shared/services/ai-edit-kickoff.service';
import { FeedMainService } from '../../pages/main/services/feed-main.service';
import { TemplateThumbnailComponent } from './template-thumbnail/template-thumbnail.component';
import { TemplatePreviewInput } from './template-thumbnail/template-preview.service';
import { ThumbnailSection } from './template-thumbnail/template-thumbnail.model';

type WizardStep = 'type' | 'template' | 'context';
type TemplateSource = 'rapidocs' | 'mine';

/** Modelo escolhido no passo 2. `blank`: documento em branco. */
type TemplateChoice =
    | { kind: 'blank' }
    | { kind: 'internal'; id: string }
    | { kind: 'company'; id: string };

/** Card da galeria, igual para modelo interno e da empresa. */
interface GalleryCard {
    key: string;
    choice: TemplateChoice;
    title: string;
    meta: string;
    sections: ThumbnailSection[];
    featured: boolean;
    preview: TemplatePreviewInput;
}

interface TypeOption {
    type: DocumentTypeEnum;
    icon: string;
    color: string;
    accent: string;
    accentLight: string;
    labelKey: string;
    hintKey: string;
}

const TYPE_OPTIONS: TypeOption[] = [
    {
        type: DocumentTypeEnum.NORMAL,
        icon: 'document-normal',
        color: 'primary',
        accent: 'var(--primary)',
        accentLight: 'var(--primaryLight)',
        labelKey: 'order.iaWizard.types.normal',
        hintKey: 'order.iaWizard.types.normalHint',
    },
    {
        type: DocumentTypeEnum.QUOTE,
        icon: 'dollar-circle',
        color: 'accentOrange',
        accent: 'var(--accentOrange)',
        accentLight: 'var(--accentOrangeLight)',
        labelKey: 'order.iaWizard.types.quote',
        hintKey: 'order.iaWizard.types.quoteHint',
    },
    {
        type: DocumentTypeEnum.CONTRACT,
        icon: 'pen-draw',
        color: 'accentGreen',
        accent: 'var(--accentGreen)',
        accentLight: 'var(--accentGreenLight)',
        labelKey: 'order.iaWizard.types.contract',
        hintKey: 'order.iaWizard.types.contractHint',
    },
];

/** Os modelos "mais usados" de cada tipo, destacados com um selo na galeria. */
const FEATURED_CODES = new Set([
    'rapidocs-inspection-report',
    'rapidocs-service-quote',
    'service-agreement',
]);

const DESCRIPTION_MAX = 600;

/**
 * Wizard "Criar com IA" (o "Começar" da tela sem seleção).
 *
 * 1. Tipo do documento.
 * 2. Modelo: os Modelos RapiDocs (galeria global) ou os da empresa, com prévia no estilo
 *    do Word, ou em branco.
 * 3. Contexto para a IA (opcional): vira a primeira mensagem do "Editar com IA".
 *
 * O documento nasce AVULSO (a tela não tem serviço selecionado). Escolhido um modelo, ele
 * abre direto no editor, já pronto. O passo 3 só existe começando em branco num documento
 * comum com o "Editar com IA" liberado: aí o editor abre no modo IA e a IA monta o documento
 * a partir do contexto.
 */
@Component({
    selector: 'create-with-ia-wizard',
    templateUrl: 'create-with-ia-wizard.component.html',
    styleUrl: 'create-with-ia-wizard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RaModalComponent, RaIconComponent, TranslatePipe, TemplateThumbnailComponent],
})
export class CreateWithIaWizardComponent implements OnInit {
    /** Texto já digitado no campo da tela, levado para o passo 3. */
    public initialPrompt = input('');
    public close = output<void>();

    // * [Injects]
    private _translate = inject(TranslateService);
    private _featureFlags = inject(FeatureFlagController);
    private _internalTemplates = inject(InternalTemplateController);
    private _templates = inject(TemplateController);
    private _documentCreate = inject(DocumentCreateController);
    private _documents = inject(DocumentController);
    private _kickoff = inject(AiEditKickoffService);
    private _feed = inject(FeedMainService);

    // * [Constantes do template]
    public readonly typeOptions = TYPE_OPTIONS;
    public readonly descriptionMax = DESCRIPTION_MAX;

    // * [Estado]
    public step = signal<WizardStep>('type');
    public type = signal<DocumentTypeEnum | null>(null);
    public source = signal<TemplateSource>('rapidocs');
    public choice = signal<TemplateChoice | null>(null);
    public description = signal('');
    public creating = signal(false);

    public loadingTemplates = signal(true);
    private _internalList = signal<InternalTemplateEntity[]>([]);
    private _companyList = signal<TemplateEntity[]>([]);

    // * [Derivados]
    public typeOption = computed(() => TYPE_OPTIONS.find(option => option.type === this.type()) ?? null);

    /**
     * O contexto para a IA só faz sentido começando EM BRANCO: com um modelo o documento já
     * nasce pronto e abre direto (o chat da IA continua no editor para quem quiser). E o
     * "Editar com IA" só edita documento comum, com a feature liberada.
     */
    public hasContextStep = computed(() => {
        const choice = this.choice();
        const type = this.type();
        // Sem tipo ainda, o caminho mais longo é o provável: o passo 1 diz "1 de 3".
        return (
            (type === null || type === DocumentTypeEnum.NORMAL) &&
            (!choice || choice.kind === 'blank') &&
            isAiEditFeatureEnabled(this._featureFlags)
        );
    });

    public steps = computed<WizardStep[]>(() =>
        this.hasContextStep() ? ['type', 'template', 'context'] : ['type', 'template']
    );
    public stepIndex = computed(() => this.steps().indexOf(this.step()));
    public isLastStep = computed(() => this.stepIndex() === this.steps().length - 1);

    public canContinue = computed(() => {
        if (this.step() === 'type') return !!this.type();
        if (this.step() === 'template') return !!this.choice();
        return true;
    });

    public rapidocsCards = computed<GalleryCard[]>(() => {
        const type = this.type();
        return this._internalList()
            .filter(template => (template.category || DocumentTypeEnum.CONTRACT) === type)
            .map(template => ({
                key: `internal-${template._id}`,
                choice: { kind: 'internal' as const, id: template._id },
                title: template.title,
                meta: this._sectionsMeta(template.sessions),
                sections: template.sessions ?? [],
                featured: FEATURED_CODES.has(template.code ?? ''),
                preview: {
                    key: `internal-${template._id}`,
                    title: template.docTitle || template.title,
                    type: template.category || DocumentTypeEnum.CONTRACT,
                    sessions: template.sessions ?? [],
                },
            }))
            .sort((a, b) => Number(b.featured) - Number(a.featured));
    });

    public companyCards = computed<GalleryCard[]>(() => {
        const type = this.type();
        return this._companyList()
            .filter(template => (template.type ?? DocumentTypeEnum.NORMAL) === type)
            .map(template => ({
                key: `company-${template._id}`,
                choice: { kind: 'company' as const, id: template._id },
                title: template.title,
                meta: this._sectionsMeta(template.sessions),
                sections: template.sessions ?? [],
                featured: false,
                preview: this._companyPreview(template),
            }));
    });

    public cards = computed(() => (this.source() === 'rapidocs' ? this.rapidocsCards() : this.companyCards()));

    public suggestions = computed(() => {
        const key = this.type() === DocumentTypeEnum.NORMAL ? 'normal' : 'other';
        const list = this._translate.instant(`order.iaWizard.context.suggestions.${key}`);
        return Array.isArray(list) ? (list as string[]) : [];
    });

    ngOnInit(): void {
        this.description.set(this.initialPrompt().slice(0, DESCRIPTION_MAX));
        this._loadTemplates();
    }

    // * [Ações]

    public pickType(type: DocumentTypeEnum): void {
        if (this.type() !== type) this.choice.set(null);
        this.type.set(type);
    }

    public pickSource(source: TemplateSource): void {
        this.source.set(source);
    }

    public pick(choice: TemplateChoice): void {
        this.choice.set(choice);
    }

    /** Duplo clique num card escolhe e já avança. */
    public pickAndContinue(choice: TemplateChoice): void {
        this.pick(choice);
        this.next();
    }

    public isChosen(choice: TemplateChoice): boolean {
        const current = this.choice();
        return !!current && this.isSelected(choice, current);
    }

    public onDescriptionInput(event: Event): void {
        const value = (event.target as HTMLTextAreaElement).value.slice(0, DESCRIPTION_MAX);
        this.description.set(value);
    }

    public useSuggestion(text: string): void {
        this.description.set(`${text}. `.slice(0, DESCRIPTION_MAX));
    }

    public next(): void {
        if (!this.canContinue() || this.creating()) return;

        if (this.isLastStep()) {
            this.create();
            return;
        }

        this.step.set(this.steps()[this.stepIndex() + 1]);
    }

    public back(): void {
        if (this.creating() || this.stepIndex() === 0) return;
        this.step.set(this.steps()[this.stepIndex() - 1]);
    }

    /** "Pular" do passo 3: cria sem contexto, a conversa começa no editor. */
    public skipContext(): void {
        this.description.set('');
        this.create();
    }

    public create(): void {
        const type = this.type();
        const choice = this.choice();
        if (!type || !choice || this.creating()) return;

        this.creating.set(true);
        this._createDocument(type, choice).subscribe({
            next: document => this._open(document),
            error: () => this.creating.set(false),
        });
    }

    /** Import de arquivos (Word, PDF...) liberado no plano: mostra o atalho no passo 1. */
    public canImport = computed(() => !this._featureFlags.isBlocked('import_files'));

    /**
     * Atalhos do passo 1 para o que não é documento avulso: a ordem de serviço e o import.
     * Os modais deles vivem na lista; o wizard fecha e pede a abertura.
     */
    public newService(): void {
        this.close.emit();
        this._feed.requestNewService();
    }

    public importFile(): void {
        this.close.emit();
        this._feed.requestImport();
    }

    public requestClose(): void {
        if (!this.creating()) this.close.emit();
    }

    // * [Internos]

    private _createDocument(type: DocumentTypeEnum, choice: TemplateChoice): Observable<DocumentEntity> {
        switch (choice.kind) {
            case 'internal':
                return this._documentCreate.insertByInternalTemplate(null, choice.id);
            case 'company':
                return this._documentCreate.insertByTemplate(null, choice.id);
            default:
                return this._documentCreate.insertStandalone(type);
        }
    }

    private _open(document: DocumentEntity): void {
        const withAi = this.hasContextStep();
        if (withAi) this._kickoff.queue(document._id, this.description());

        this.close.emit();
        this._documents.navigateToDocument(document, true, withAi ? { mode: 'ai' } : undefined);
    }

    private _loadTemplates(): void {
        this.loadingTemplates.set(true);
        let pending = 2;
        const done = () => {
            if (--pending === 0) this.loadingTemplates.set(false);
        };

        this._internalTemplates.getAll().subscribe({
            next: list => this._internalList.set(list ?? []),
            error: done,
            complete: done,
        });
        this._templates.getAll().subscribe({
            next: list => this._companyList.set((list ?? []).filter(template => !template.delete)),
            error: done,
            complete: done,
        });
    }

    /** A API devolve o modelo da empresa com config, cabeçalho e rodapé próprios. */
    private _companyPreview(template: TemplateEntity): TemplatePreviewInput {
        const raw = template as TemplateEntity & {
            documentConfig?: Record<string, unknown>;
            header?: unknown[];
            footer?: unknown[];
        };

        return {
            key: `company-${template._id}`,
            title: template.docTitle || template.title,
            type: template.type ?? DocumentTypeEnum.NORMAL,
            sessions: template.sessions ?? [],
            documentConfig: raw.documentConfig,
            header: raw.header,
            footer: raw.footer,
        };
    }

    private _sectionsMeta(sessions: ThumbnailSection[] | undefined): string {
        const count = sessions?.length ?? 0;
        return this._translate.instant(
            count === 1 ? 'order.iaWizard.template.sectionsOne' : 'order.iaWizard.template.sectionsMany',
            { count }
        );
    }

    private isSelected(a: TemplateChoice, b: TemplateChoice): boolean {
        if (a.kind !== b.kind) return false;
        if (a.kind === 'blank') return true;
        return a.id === (b as { id: string }).id;
    }
}
